import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './OrderEquipmentForm.css';

const getInitialFormData = () => ({
  vendor: '',
  model: '',
  type: '',
  profile: '',
  preAssignTo: '',
  quantity: 1,
  alternateDeliveryAddress: false,
  deliveryCompany: '',
  deliveryAtt: '',
  deliveryAddress: '',
  deliveryPostalCode: '',
  deliveryCity: '',
  deliveryCvrVat: ''
});

const OrderEquipmentForm = ({ onBack }) => {
  const { selectedTenant, authenticatedFetch, availableTenants } = useAuth();
  const [formData, setFormData] = useState(getInitialFormData);

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [approvedProfiles, setApprovedProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const activeTenantName = useMemo(() => {
    if (selectedTenant?.name) {
      return selectedTenant.name;
    }
    if (Array.isArray(availableTenants) && availableTenants.length === 1) {
      return availableTenants[0].name;
    }
    return '';
  }, [selectedTenant, availableTenants]);

  const profileOptions = useMemo(() => {
    const names = approvedProfiles
      .map((profile) => profile.profile)
      .filter((name) => typeof name === 'string' && name.trim());
    const unique = Array.from(new Set(names.map((name) => name.trim())));
    if (!unique.includes('Other')) {
      unique.push('Other');
    }
    return unique;
  }, [approvedProfiles]);

  const fetchApprovedProfiles = useCallback(async (tenantName = activeTenantName) => {
    if (!tenantName) {
      setApprovedProfiles([]);
      return;
    }

    try {
      setProfilesLoading(true);
      setProfilesError(null);

      const params = new URLSearchParams({
        tenant: tenantName
      });
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/approved-hardware?${params.toString()}`);
      const data = await response.json();

      if (response.ok) {
        const profiles = Array.isArray(data.profiles) ? data.profiles : [];
        setApprovedProfiles(profiles);
      } else {
        setProfilesError(data.error || 'Unable to load approved profiles');
        setApprovedProfiles([]);
      }
    } catch (profileError) {
      console.error('Error fetching approved hardware profiles:', profileError);
      setProfilesError('Unable to load approved profiles');
      setApprovedProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, [activeTenantName, authenticatedFetch]);

  const fetchTenantUsers = useCallback(async (tenantName = activeTenantName) => {
    if (!tenantName) {
      setTenantUsers([]);
      return;
    }
    try {
      setUsersLoading(true);
      const params = new URLSearchParams({ tenant: tenantName, limit: '500' });
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenant-users?${params.toString()}`);
      const data = await response.json();
      if (response.ok && data.success && Array.isArray(data.users)) {
        setTenantUsers(data.users);
      } else {
        setTenantUsers([]);
      }
    } catch {
      setTenantUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [activeTenantName, authenticatedFetch]);

  const fetchAssets = useCallback(async (tenantName = activeTenantName) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching assets for tenant:', tenantName);
      
      if (!tenantName) {
        setError('No tenant available');
        return;
      }
      
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/assets?tenant=${encodeURIComponent(tenantName)}`);
      const data = await response.json();

      console.log('Response status:', response.status);
      console.log('Response data:', data);
      
      if (response.ok) {
        console.log('All assets received:', data.assets);
        console.log('Total assets count:', data.assets.length);
        
        // Log the table structure to see all available fields
        if (data.columns) {
          console.log('=== ASSETS TABLE STRUCTURE ===');
          console.log('Available columns:', data.columns);
          data.columns.forEach(col => {
            console.log(`- ${col.field} (${col.type}) - Null: ${col.null}, Key: ${col.key}`);
          });
          console.log('=== END TABLE STRUCTURE ===');
        }
        
        // Debug: Log the first few assets to see their structure
        if (data.assets && data.assets.length > 0) {
          console.log('First asset structure:', data.assets[0]);
          console.log('Available fields:', Object.keys(data.assets[0]));
          
          // Look for asset with ID 46 specifically
          const asset46 = data.assets.find(asset => asset.id === 46);
          if (asset46) {
            console.log('=== ASSET ID 46 FOUND ===');
            console.log('Full asset data:', asset46);
            console.log('lastconnected value:', asset46.lastconnected);
            console.log('warranty-end value:', asset46['warranty-end']);
            console.log('All field names:', Object.keys(asset46));
            
            // Check different possible field names
            console.log('lastconnected variations:');
            console.log('- lastconnected:', asset46.lastconnected);
            console.log('- last_connected:', asset46.last_connected);
            console.log('- LastConnected:', asset46.LastConnected);
            console.log('- last-connected:', asset46['last-connected']);
            
            console.log('warranty-end variations:');
            console.log('- warranty-end:', asset46['warranty-end']);
            console.log('- warranty_end:', asset46.warranty_end);
            console.log('- WarrantyEnd:', asset46.WarrantyEnd);
            console.log('- warranty_end:', asset46['warranty_end']);
            console.log('=== END ASSET ID 46 ===');
          } else {
            console.log('Asset with ID 46 not found');
            console.log('Available asset IDs:', data.assets.map(a => a.id).slice(0, 10));
          }
        }
        
        // Filter assets: lastconnected older than 60 days AND valid warranty
        const filteredAssets = data.assets.filter(asset => {
          // Special debugging for asset ID 46
          if (asset.id === 46) {
            console.log('=== FILTERING ASSET ID 46 ===');
            console.log('Full asset data:', asset);
          }
          
          console.log('Processing asset:', asset);
          
          // Use correct field names from database schema
          const lastConnectedValue = asset.LastConnected;
          const warrantyEndValue = asset['warranty-end'];
          
          if (asset.id === 46) {
            console.log('Asset 46 - lastconnected value:', lastConnectedValue);
            console.log('Asset 46 - warranty-end value:', warrantyEndValue);
          }
          
          console.log('lastconnected value:', lastConnectedValue);
          console.log('warranty-end value:', warrantyEndValue);
          
          // Check if we have valid values
          if (!lastConnectedValue || !warrantyEndValue) {
            console.log('Missing required fields - skipping asset');
            return false;
          }
          
          const lastConnected = new Date(lastConnectedValue);
          const warrantyEnd = new Date(warrantyEndValue);
          const now = new Date();
          const sixtyDaysAgo = new Date(now.getTime() - (60 * 24 * 60 * 60 * 1000));
          
          if (asset.id === 46) {
            console.log('Asset 46 - lastConnected date:', lastConnected);
            console.log('Asset 46 - warrantyEnd date:', warrantyEnd);
            console.log('Asset 46 - sixtyDaysAgo date:', sixtyDaysAgo);
            console.log('Asset 46 - now date:', now);
            console.log('Asset 46 - lastConnected timestamp:', lastConnected.getTime());
            console.log('Asset 46 - sixtyDaysAgo timestamp:', sixtyDaysAgo.getTime());
            console.log('Asset 46 - warrantyEnd timestamp:', warrantyEnd.getTime());
            console.log('Asset 46 - now timestamp:', now.getTime());
          }
          
          console.log('lastConnected date:', lastConnected);
          console.log('warrantyEnd date:', warrantyEnd);
          console.log('sixtyDaysAgo date:', sixtyDaysAgo);
          console.log('now date:', now);
          
          // Check if dates are valid
          const isLastConnectedValid = !isNaN(lastConnected.getTime());
          const isWarrantyEndValid = !isNaN(warrantyEnd.getTime());
          
          if (asset.id === 46) {
            console.log('Asset 46 - isLastConnectedValid:', isLastConnectedValid);
            console.log('Asset 46 - isWarrantyEndValid:', isWarrantyEndValid);
          }
          
          console.log('isLastConnectedValid:', isLastConnectedValid);
          console.log('isWarrantyEndValid:', isWarrantyEndValid);
          
          if (!isLastConnectedValid || !isWarrantyEndValid) {
            console.log('Invalid dates - skipping asset');
            return false;
          }
          
          const isLastConnectedOld = lastConnected < sixtyDaysAgo;
          const isWarrantyValid = warrantyEnd > now;
          
          if (asset.id === 46) {
            console.log('Asset 46 - isLastConnectedOld:', isLastConnectedOld);
            console.log('Asset 46 - isWarrantyValid:', isWarrantyValid);
            console.log('Asset 46 - days since last connected:', Math.floor((now - lastConnected) / (1000 * 60 * 60 * 24)));
            console.log('Asset 46 - days until warranty expires:', Math.floor((warrantyEnd - now) / (1000 * 60 * 60 * 24)));
            console.log('Asset 46 - Asset qualifies:', isLastConnectedOld && isWarrantyValid);
            console.log('=== END FILTERING ASSET ID 46 ===');
          }
          
          console.log('isLastConnectedOld:', isLastConnectedOld);
          console.log('isWarrantyValid:', isWarrantyValid);
          console.log('Asset qualifies:', isLastConnectedOld && isWarrantyValid);
          console.log('---');
          
          return isLastConnectedOld && isWarrantyValid;
        });
        
        console.log('Filtered assets count:', filteredAssets.length);
        console.log('Filtered assets:', filteredAssets);
        
        setAssets(filteredAssets);
      } else {
        console.error('API Error:', data);
        setError(data.error || `Failed to fetch assets (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setError('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [activeTenantName, authenticatedFetch]);

  useEffect(() => {
    if (activeTenantName) {
      fetchAssets(activeTenantName);
      fetchApprovedProfiles(activeTenantName);
      fetchTenantUsers(activeTenantName);
    } else {
      setAssets([]);
      setApprovedProfiles([]);
      setTenantUsers([]);
    }
  }, [activeTenantName, fetchAssets, fetchApprovedProfiles, fetchTenantUsers]);

  const handleProfileSelection = (value) => {
    const matchedProfile = approvedProfiles.find((profile) => profile.profile === value);
    if (matchedProfile) {
      setFormData(prev => ({
        ...prev,
        profile: value,
        vendor: matchedProfile.vendor || '',
        model: matchedProfile.model || '',
        type: matchedProfile.type || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        profile: value
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'alternateDeliveryAddress') {
      setFormData((prev) =>
        checked
          ? { ...prev, alternateDeliveryAddress: true }
          : {
              ...prev,
              alternateDeliveryAddress: false,
              deliveryCompany: '',
              deliveryAtt: '',
              deliveryAddress: '',
              deliveryPostalCode: '',
              deliveryCity: '',
              deliveryCvrVat: ''
            }
      );
      return;
    }
    if (name === 'quantity') {
      const n = parseInt(value, 10);
      setFormData((prev) => ({
        ...prev,
        quantity: Number.isFinite(n) && n >= 1 ? Math.min(n, 999) : 1
      }));
      return;
    }
    if (name === 'profile') {
      handleProfileSelection(value);
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssetClick = (asset) => {
    setFormData(prev => ({
      ...prev,
      vendor: asset.manufacturer || '',
      model: asset.model || '',
      type: asset.serial || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (!activeTenantName) {
      setSubmitMessage('Error: No tenant available');
      setIsSubmitting(false);
      return;
    }

    if (formData.alternateDeliveryAddress) {
      const missing = [];
      if (!String(formData.deliveryCompany || '').trim()) missing.push('Company');
      if (!String(formData.deliveryAddress || '').trim()) missing.push('Address');
      if (!String(formData.deliveryPostalCode || '').trim()) missing.push('Post code');
      if (!String(formData.deliveryCity || '').trim()) missing.push('City');
      if (missing.length) {
        setSubmitMessage(`❌ Please fill in the delivery form: ${missing.join(', ')}`);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await authenticatedFetch('/api/create-equipment-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenant: activeTenantName,
          formData: formData
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage('✅ Successful - Your equipment order has been processed');
        setTicketUrl(data.ticketUrl);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData(getInitialFormData());
          setSubmitMessage('');
          setTicketUrl('');
        }, 5000);
      } else {
        const errorMessage = data.error || 'Failed to process your request';
        console.error('Server error:', errorMessage, data);
        setSubmitMessage(`❌ ${errorMessage}`);
        setTicketUrl('');
      }
    } catch (error) {
      console.error('Error creating equipment order:', error);
      setSubmitMessage('❌ Failed to process your request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };


  return (
    <div className="order-equipment-container">
      <div className="form-header">
        <button onClick={onBack} className="back-button">
          <Icon name="arrowUp" size="small" />
          Back to Self Service
        </button>
        <h2>Order New Equipment</h2>
        <p>Request new hardware, software licenses, or IT equipment for your team</p>
      </div>

      <div className="form-content">
        <form onSubmit={handleSubmit} className="equipment-form">
          <div className="form-section">
            <h3>Equipment Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="profile">Profile *</label>
                <select
                  id="profile"
                  name="profile"
                  value={formData.profile}
                  onChange={handleInputChange}
                  required
                  disabled={profilesLoading}
                >
                  <option value="">Select a profile</option>
                  {profileOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {profilesLoading && (
                  <div className="input-hint">Loading approved profiles…</div>
                )}
                {profilesError && (
                  <div className="input-error">{profilesError}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="vendor">Vendor *</label>
                <input
                  type="text"
                  id="vendor"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Dell, HP, Lenovo"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="model">Model *</label>
                <input
                  type="text"
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., OptiPlex 7090, ThinkPad X1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="type">Type *</label>
                <input
                  type="text"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Laptop, Desktop, Monitor, Printer"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quantity">Equipment quantity *</label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min={1}
                  max={999}
                  step={1}
                  required
                />
              </div>
            </div>
            <div className="form-group form-group-checkbox">
              <input
                type="checkbox"
                id="alternateDeliveryAddress"
                name="alternateDeliveryAddress"
                checked={formData.alternateDeliveryAddress}
                onChange={handleInputChange}
              />
              <label htmlFor="alternateDeliveryAddress">Change delivery address</label>
            </div>
            {formData.alternateDeliveryAddress && (
              <div className="shipping-section" role="region" aria-label="Delivery address">
                <h4 className="shipping-section-title">Delivery address</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryCompany">Company *</label>
                    <input
                      type="text"
                      id="deliveryCompany"
                      name="deliveryCompany"
                      value={formData.deliveryCompany}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="organization"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="deliveryAtt">ATT</label>
                    <input
                      type="text"
                      id="deliveryAtt"
                      name="deliveryAtt"
                      value={formData.deliveryAtt}
                      onChange={handleInputChange}
                      placeholder="Attention / contact"
                      autoComplete="name"
                    />
                  </div>
                </div>
                <div className="form-row form-row-full">
                  <div className="form-group">
                    <label htmlFor="deliveryAddress">Address *</label>
                    <input
                      type="text"
                      id="deliveryAddress"
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="street-address"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryPostalCode">Post code *</label>
                    <input
                      type="text"
                      id="deliveryPostalCode"
                      name="deliveryPostalCode"
                      value={formData.deliveryPostalCode}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="postal-code"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="deliveryCity">City *</label>
                    <input
                      type="text"
                      id="deliveryCity"
                      name="deliveryCity"
                      value={formData.deliveryCity}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="address-level2"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryCvrVat">CVR / VAT</label>
                    <input
                      type="text"
                      id="deliveryCvrVat"
                      name="deliveryCvrVat"
                      value={formData.deliveryCvrVat}
                      onChange={handleInputChange}
                      placeholder="Company registration / VAT ID"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="preAssignTo">Pre-assign to user</label>
                <select
                  id="preAssignTo"
                  name="preAssignTo"
                  value={formData.preAssignTo}
                  onChange={handleInputChange}
                  disabled={usersLoading}
                >
                  <option value="">— None —</option>
                  {tenantUsers.map((user, index) => {
                    const displayName = user.displayName || user.displayname || user.userPrincipalName || user.userprincipalname || '—';
                    const key = user.userPrincipalName || user.userprincipalname || `${displayName}-${index}`;
                    return (
                      <option key={key} value={displayName}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
                {usersLoading && (
                  <div className="input-hint">Loading users…</div>
                )}
                <small className="input-hint">Optional: Assign equipment to a user when delivered</small>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onBack} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-button">
              {isSubmitting ? 'Submitting...' : 'Submit Order'}
            </button>
          </div>

          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Error') || submitMessage.startsWith('❌') ? 'error' : 'success'}`}>
              <Icon name={submitMessage.includes('Error') || submitMessage.startsWith('❌') ? 'exclamation' : 'checkmark'} size="small" />
              <div>
                <div>{submitMessage}</div>
                {ticketUrl && (
                  <div className="ticket-link">
                    <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="ticket-url">
                      <Icon name="external" size="small" />
                      View Ticket in Zendesk
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      {(loading || error || assets.length > 0) && (
        <div className="assets-section">
          <div className="assets-header">
            <h3>Reusable asset candidates</h3>
            <p>Assets that haven't been connected in over 60 days and have valid warranty. Click on any row to fill the form.</p>
            <button onClick={() => fetchAssets(activeTenantName)} className="refresh-button" disabled={loading || !activeTenantName}>
              <Icon name="refresh" size="small" />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div className="loading-message">
              <Icon name="refresh" size="medium" />
              <span>Loading assets...</span>
            </div>
          ) : error ? (
            <div className="error-message">
              <Icon name="exclamation" size="medium" />
              <span>{error}</span>
            </div>
          ) : (
            <div className="assets-grid">
              <div className="assets-table-header">
                <div className="asset-cell">Serial Number</div>
                <div className="asset-cell">Vendor</div>
                <div className="asset-cell">Model</div>
                <div className="asset-cell">Last Connected</div>
                <div className="asset-cell">Warranty End</div>
              </div>
              {assets.map((asset, index) => (
                <div key={asset.id || index} className="asset-row clickable-asset-row" onClick={() => handleAssetClick(asset)}>
                  <div className="asset-cell">{asset.serial || 'N/A'}</div>
                  <div className="asset-cell">{asset.manufacturer || 'N/A'}</div>
                  <div className="asset-cell">{asset.model || 'N/A'}</div>
                  <div className="asset-cell">{formatDate(asset.LastConnected)}</div>
                  <div className="asset-cell">{formatDate(asset['warranty-end'])}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderEquipmentForm;
